import type { VoidFunctionComponent } from 'react';

import { MULTI_ORG, SCIM_PROVISIONING } from '../../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../../helpers/features/featureFlags';
import { AccessGate } from '../../../generic';
import { scimFeature } from '../../billing/features/consts';
import OrganizationSettingsPermissionGate from '../../OrganizationSettingsPermissionGate';
import { shouldDisplayScimSettings } from '../../shared/permissions';

import ScimProvisioning from './ScimProvisioning';

/**
 * SCIM provisioning settings. The tab is permission-gated rather than gated on `SCIM_PROVISIONING`, so an org
 * without that flag still lands here and is shown the paid feature gate — `verify` drives the gate off the flag
 * instead of a subscription tier, since the flag is what the API actually enforces.
 *
 * `MULTI_ORG` is different, and is handled by rendering nothing rather than by the gate: it is not something an
 * org can buy, so offering to sell it would be a lie. SCIM does not exist on a deployment without multi-org (the
 * API and the token routes both refuse), so neither does this page. The nav item is hidden on the same condition.
 */
export const ScimSettingsContainer: VoidFunctionComponent = () => {
  const isScimEnabled = useIsFeatureEnabled(SCIM_PROVISIONING);
  const isMultiOrgEnabled = useIsFeatureEnabled(MULTI_ORG);

  if (!isMultiOrgEnabled) {
    return null;
  }

  return (
    <OrganizationSettingsPermissionGate checkPermission={shouldDisplayScimSettings}>
      <AccessGate level="enterprise" behavior="showPage" focusFeature={scimFeature} verify={() => isScimEnabled}>
        <ScimProvisioning />
      </AccessGate>
    </OrganizationSettingsPermissionGate>
  );
};
